import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  TextInput,
  Text,
  Platform,
  AppState,
} from 'react-native';

import Animated, {
  useSharedValue,
  withTiming,
  useAnimatedStyle,
  Easing as ReanimatedEasing,
  interpolate,
} from 'react-native-reanimated';

import { ls } from '../styles';
import FadeInAnimation from '../../FadinAnimation/FadinAnimation';
import {
  Theme,
  themeColorSet,
  ThemedPressable,
  ThemedView,
} from '@app/theme';
import { ThemedText } from '@app/theme/Components';
import { useDarkMode } from '@app/utils';
import { useTheme } from '@shopify/restyle';


const InputText = forwardRef((props: any, ref) => {
  const {
    height = 48,
    placeHolder = null,
    maxLength = null,
    keyboardType = 'default',
    value = null,
    onChangeText = () => {},
    onChange = () => {},
    onFocus = () => {},
    onBlur = () => {},
    showLeftAccessory,
    leftAccessory,
    showRightAccessory,
    rightAccessory,
    overRideContainerStyles,
    overRideInputStyles,
    overRideLabelStyles,
    showLabel,
    label,
    showErrorMessage = false,
    showErrorMessageInFocus = false,
    hasError = '',
    showAnimation = false,
    autoFocus = false,
    autoCapitalize = 'none',
    multiline = false,
    numberOfLines = 1,
    secureTextEntry = false,
    editable = true,
    successMessage = '',
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [isBlurred, setIsBlurred] = useState(false);

  const isDarkMode = useDarkMode();
  const { colors } = useTheme<Theme>();

  const innerRef = useRef<TextInput>(null);
  useImperativeHandle(ref, () => innerRef.current as TextInput);

  const appStateRef = useRef(AppState.currentState);
  const [appState, setAppState] = useState(AppState.currentState);

  useEffect(() => {
    const sub = AppState.addEventListener('change', next => {
      setAppState(next);
      appStateRef.current = next;
    });
    return () => sub.remove();
  }, []);

  const focusAnim = useSharedValue(0);

  useEffect(() => {
    if (appState === 'active') {
      if (showAnimation) {
        focusAnim.value = withTiming(
          isFocused || !!value ? 1 : 0,
          {
            duration: 150,
            easing: ReanimatedEasing.bezier(0.4, 0, 0.2, 1),
          }
        );
      } else {
        focusAnim.value = withTiming(1, { duration: 0 });
      }
    }
  }, [isFocused, value, showAnimation, appState]);

  // Label animated style (Reanimated)
  const labelAnimatedStyle = useAnimatedStyle(() => {
    const scale = interpolate(focusAnim.value, [0, 1], [1, 0.75]);
    const translateY = interpolate(
      focusAnim.value,
      [0, 1],
      [0, -(33 + Math.abs((height - 48) / 2))]
    );
    const translateX = interpolate(
      focusAnim.value,
      [0, 1],
      [showLeftAccessory ? 26 : 0, -10]
    );

    return {
      transform: [
        { scale },
        { translateY },
        { translateX },
      ],
    };
  });

  const defaultClass = isFocused
    ? (showErrorMessageInFocus && Boolean(hasError) && ls.inputError) ||
      ls.inputFocused
    : isBlurred && Boolean(hasError) && ls.inputError;

  const displayError = showErrorMessageInFocus
    ? Boolean(hasError)
    : Boolean(hasError) && !isFocused && isBlurred;

  const showPlaceholder = showAnimation ? isFocused : true;

  return (
    <ThemedView style={[overRideContainerStyles]}>
      <ThemedPressable
        style={{
          outline: 'none',
        }}
        height={height}
        onFocus={() => {
          setTimeout(() => {
            innerRef?.current?.focus();
          }, 100);
        }}
        backgroundColor={
          editable ? 'bg-surface-primary' : 'bg-action-disabled'
        }
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        borderColor={
          !editable
            ? 'border-form'
            : Boolean(hasError)
            ? 'border-error'
            : isFocused
            ? 'border-focus'
            : 'border-form'
        }
        borderWidth={1}
        borderRadius="xs"
        mt="sm"
        py="md"
        px="lg"
        width={'100%'}
        {...(multiline && {
          height: 100,
          alignItems:
            Platform.OS === 'web' ? 'flex-start' : 'flex-end',
        })}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        onPress={() => {
          innerRef?.current?.focus();
        }}>
        
        {showLabel && (
          <Animated.View
            style={[
              ls.labelContainer,
              {
                zIndex: 1,
                marginLeft: Platform.OS !== 'web' ? 15 : 0,
              },
              labelAnimatedStyle,
            ]}>
            <ThemedView
              pointerEvents="none"
              ml={Platform.OS === 'web' ? undefined : 'xs'}
              backgroundColor="bg-surface-primary"
              px="xs">
              <ThemedText
                style={[
                  ls.label,
                  isDarkMode
                    ? { color: 'white' }
                    : { color: colors['text-placeholder'] },
                  defaultClass,
                ]}>
                {label}
              </ThemedText>
            </ThemedView>
          </Animated.View>
        )}

        {showLeftAccessory && leftAccessory && leftAccessory()}

        <TextInput
          placeholderTextColor={'#98a2b3'}
          autoCapitalize={autoCapitalize}
          ref={innerRef}
          autoCorrect={false}
          style={[
            ls.input,
            overRideInputStyles,
            {
              color:
                Platform.OS !== 'web'
                  ? 'black'
                  : isDarkMode
                  ? 'white'
                  : themeColorSet['text-primary'],
              backgroundColor: !editable
                ? themeColorSet['bg-action-disabled']
                : themeColorSet['bg-fill-primary'],
            },
          ]}
          keyboardType={keyboardType}
          autoFocus={autoFocus}
          maxLength={maxLength}
          placeholder={showPlaceholder ? placeHolder : ''}
          value={value}
          nativeID={label || placeHolder}
          multiline={multiline}
          numberOfLines={numberOfLines}
          id={label || placeHolder}
          editable={editable}
          onChangeText={onChangeText}
          onChange={onChange}
          onFocus={() => {
            setIsFocused(true);
            setIsBlurred(false);
            onFocus?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            setIsBlurred(true);
            onBlur?.();
          }}
        />

        {showRightAccessory && rightAccessory && rightAccessory()}
      </ThemedPressable>

      {/* ERROR MESSAGE */}
      {showErrorMessage && (
        <ThemedView height={18} mt="xs" mb="sm">
          {displayError && (
            <FadeInAnimation delay={100} duration={500}>
              <ThemedText variant="b4r" color="text-error">
                {displayError ? hasError : ''}
              </ThemedText>
            </FadeInAnimation>
          )}
        </ThemedView>
      )}

      {/* SUCCESS MESSAGE */}
      {successMessage && (
        <ThemedView height={18} mt="xs" mb="sm">
          <FadeInAnimation delay={100} duration={300}>
            <ThemedText variant="b4r" color="text-success">
              {successMessage}
            </ThemedText>
          </FadeInAnimation>
        </ThemedView>
      )}
    </ThemedView>
  );
});

export default InputText; 
